import { useEffect, type PropsWithChildren } from "react";

import { handleComponentError } from "initSentry";
import { ErrorBoundary, type FallbackProps } from "react-error-boundary";

import { ErrorFallback } from "app/components/fallbacks";
import { Unauthorized } from "app/views/Unauthorized";

const GENERIC_MESSAGE =
  "Unable to load the application. You may not have the necessary permissions or there may be a system error. If the problem persists, please contact Stacklet support.";

// "not authorized" GraphQL errors surface in the error message and source.errors.
const AUTHORIZATION_ERROR =
  /not authorized|unauthorized|forbidden|not permitted/i;

interface RelayErrorSource {
  source?: { errors?: readonly ({ message?: string } | null)[] | null };
}

// Backstop for the shell failing to load: an authorization error becomes the
// unauthorized screen, anything else the generic error page.
//
// This only ever sees shell-bootstrap errors (the AppQuery `platform` fetch and
// the ViewerGate whoami), because App wraps its page region in its own generic
// ErrorBoundary — so a resource-specific auth error from a routed page is caught
// there and never reaches this classifier. The invariant is covered by the
// "leaves a downstream boundary to handle page errors" test.
export function UnauthorizedBoundary({ children }: PropsWithChildren) {
  return (
    <ErrorBoundary FallbackComponent={AppErrorFallback}>
      {children}
    </ErrorBoundary>
  );
}

function AppErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  if (isAuthorizationError(error)) {
    return <Unauthorized />;
  }

  return (
    <GenericAppError error={error} resetErrorBoundary={resetErrorBoundary} />
  );
}

function isAuthorizationError(error: unknown): boolean {
  if (typeof error !== "object" || error === null) {
    return false;
  }

  const messages: string[] = [];

  const { message } = error as { message?: unknown };
  if (typeof message === "string") {
    messages.push(message);
  }

  const { source } = error as RelayErrorSource;
  for (const gqlError of source?.errors ?? []) {
    if (typeof gqlError?.message === "string") {
      messages.push(gqlError.message);
    }
  }

  return messages.some((message) => AUTHORIZATION_ERROR.test(message));
}

// Non-auth error: show the generic page and log it, since we catch it before
// the root boundary would.
function GenericAppError({
  error,
  resetErrorBoundary,
}: Pick<FallbackProps, "error" | "resetErrorBoundary">) {
  useEffect(() => {
    handleComponentError(
      error instanceof Error ? error : new Error(String(error)),
      {},
    );
  }, [error]);

  return (
    <ErrorFallback
      message={GENERIC_MESSAGE}
      resetErrorBoundary={resetErrorBoundary}
      tryAgain
    />
  );
}
